import {
    IVideoPlayerViewModel,
    IVideoPlayOptions,
    IVideoPreloadOptions,
    IVideoResolution
} from "./video-player.view-model.interface";
import {VideoElementContainer} from "./video-element-container";
import {IVideoPlayerStrategy} from "./video-player.strategy.interface";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import React, {CSSProperties} from "react";
import {CGPEventSubscription} from "../../sdk/cgp-sdk.interface";
import {IReactionDisposer, makeObservable, observable, reaction, runInAction} from "mobx";
import {VideoEventsController} from "./video-events-controller";
import {NullableUndefinedString} from "../../../../common/types/nullable-types";
import {imagePreload} from "../../../../common/utils/image-preload";
import {TimeSpan} from "@sparkware/app-utils";
import {VideoConsoleAnalyticsReporter} from "./analytics/video-console-analytics-reporter";
import {isVideoFatalError, IVideoAnalyticsReporter} from "./analytics/video-analytics-reporter.interface";

export abstract class VideoPlayerBaseModel implements IVideoPlayerViewModel {

    constructor(protected readonly services: IServiceFactoryExtended) {
        this._videoElement = this._createVideoElement();
        this._videoElementContainer = new VideoElementContainer(this._videoElement);
        this._analyticsReporter = VideoPlayerBaseModel.DefaultAnalyticsReporter;
        this._eventsController = new VideoEventsController(this._videoElement,  () => this._analyticsReporter);

        this._sdkEventsSubscriptions.push(this.services.sdk.events.onAppPaused(this._onAppPaused));
        this._reactions.push(reaction(() => services.video.isSoundMuted, (isMuted) => {
            if (isMuted) {
                this.mute();
            } else {
                this.unmute();
            }
        }));

        this._reactions.push(reaction(
            () => this.watchTimeLimitReached,
            async (watchLimitReached) => {
                if (watchLimitReached) {
                    await this.pause();
                    this._analyticsReporter.reportWatchTimeLimitReached();
                }
            }));

        makeObservable(this);

    }

    private static DefaultAnalyticsReporter: IVideoAnalyticsReporter = new VideoConsoleAnalyticsReporter();

    private _videoElement: HTMLVideoElement;
    private _sdkEventsSubscriptions: CGPEventSubscription[] = [];
    private _reactions: IReactionDisposer[] = [];
    private _videoElementContainer: VideoElementContainer;
    private _src: string = "";
    private _analyticsReporter: IVideoAnalyticsReporter;

    @observable.ref
    private _watchTimeLimit: TimeSpan | undefined = undefined;



    protected abstract _createPlayerStrategy(): IVideoPlayerStrategy;


    protected get videoElement(): HTMLVideoElement {
        return this._videoElement;
    }

    private _eventsController: VideoEventsController;

    attachAnalyticsReporter(reporter: IVideoAnalyticsReporter): void {
        this._analyticsReporter = reporter;
    }

    protected _createVideoElement(): HTMLVideoElement {
        const videoElement = document.createElement('video');
        videoElement.playsInline = true;
        videoElement.muted = this.services.video.isSoundMuted;
        videoElement.autoplay = false;
        videoElement.loop = true;
        videoElement.preload = "auto";
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        return videoElement;
    }

    private _playStrategy: IVideoPlayerStrategy | null = null;
    protected get playStrategy(): IVideoPlayerStrategy {

        if(!this._videoElementContainer.isAttached) {
            throw new Error('Cannot create play strategy before the player is attached to the DOM. Call the attach method first');
        }

        if(!this._playStrategy) {
            this._playStrategy = this._createPlayerStrategy();
        }

        return this._playStrategy;
    }

    get resolution(): IVideoResolution {
        return {
            width: this.videoElement.videoWidth,
            height: this.videoElement.videoHeight
        };
    }

    @observable.ref
    private _isPosterLoaded: boolean = false;
    get isPosterLoaded(): boolean {
        return this._isPosterLoaded;
    }

    private set isPosterLoaded(value: boolean) {
        runInAction(() => {
            this._isPosterLoaded = value;
        })

    }

    get isLoading(): boolean {
        return this._eventsController.isLoading;
    }

    get isBuffering(): boolean {
        return this._eventsController.isBuffering;
    }

    get hasError(): boolean {
        return this._eventsController.hasError;
    }

    get isPlaying(): boolean {
        return this._eventsController.isPlaying;
    }
    get isPaused(): boolean {
        return this._eventsController.isPaused;
    }
    
    get isMuted(): boolean {
        return this.videoElement.muted;
    }

    private _onAppPaused = () => {
        if(this._videoElementContainer.isAttached && this.playStrategy.isPlaying) {
            this.playStrategy.pause();
            this._analyticsReporter.reportPauseOnAppSleep();
            const appResumeSubscription = this.services.sdk.events.onAppResumed(async () => {
                appResumeSubscription.off();
                const subscriptionIndex = this._sdkEventsSubscriptions.findIndex(s => s === appResumeSubscription);
                if(subscriptionIndex >= 0) {
                    this._sdkEventsSubscriptions.splice(subscriptionIndex, 1);
                }

                await this.resume();
            });

            this._sdkEventsSubscriptions.push(appResumeSubscription);
        }
    }


    attach(containerElement: HTMLElement, videoElementStyles?: CSSProperties): void {
        if(videoElementStyles) {
            this.applyVideoElementStyles(videoElementStyles);
        }
        this._videoElementContainer.attach(containerElement);
    }

    detach(): void {
        this._videoElementContainer.detach();
    }

    applyVideoElementStyles(styles: React.CSSProperties) {
        Object.keys(styles).forEach(styleProp => {
            this._videoElement.style[styleProp] = styles[styleProp];
        });
    }


    destroy(): void {
        this._videoElementContainer.destroy();
        this._eventsController.destroy();

        this._sdkEventsSubscriptions.forEach(sdkEvent => {
            sdkEvent.off();
        });
        this._sdkEventsSubscriptions = [];

        this._reactions.forEach(r => r());
        this._reactions = [];

        this._playStrategy?.destroy();
        this._playStrategy = null;

        this._videoElement.src = "";
        this._videoElement.load();
        this._videoElement = null;
    }

    setPoster(poster: NullableUndefinedString): void {
        if(poster) {
            if(poster !== this._videoElement.poster) {
                this._videoElement.poster = "";
                this.isPosterLoaded = false;
                imagePreload(poster)
                    .then(() => {
                        this._videoElement.poster = poster;
                        this.isPosterLoaded = true;
                    })
                    .catch((err) => {
                        this._analyticsReporter.reportPosterLoadFailure(err);
                    });
            }
        } else {
            this._videoElement.poster = "";
            this.isPosterLoaded = false;
        }
    }

    private _resetWatchTime(src: string): void {
        if(this._src !== src) {
            this._eventsController.resetWatchTime();
            this._src = src;
        }
    }

    get watchTime(): TimeSpan {
        return this._eventsController.watchTime;
    }

    get videoDurationInSeconds(): number {
        return this.videoElement.duration
    }

    get watchTimeLimitReached(): boolean {
        if(this._watchTimeLimit) {
            return this.watchTime.totalMilliseconds >= this._watchTimeLimit.totalMilliseconds;
        } else {
            return false;
        }
    }

    async preload(src: string, options: IVideoPreloadOptions): Promise<void> {
        try {

            this._resetWatchTime(src);

            if(this.watchTimeLimitReached) {
                return;
            }

            if(!this._videoElement.muted) {
                this._videoElement.muted = true;
            }

            this.setPoster(options.poster);

            await this.playStrategy.preload(src);
        } catch (err){
            if(isVideoFatalError(err)) {
                this._analyticsReporter.reportPreloadError(err);
                throw err;
            }
        }

    }

    async play(src: string, options: IVideoPlayOptions): Promise<void> {
        try {

            this._resetWatchTime(src);

            runInAction(() => {
                this._watchTimeLimit = options.watchTimeLimit
            });

            if(this.watchTimeLimitReached) {
                return;
            }

            this.setPoster(options.poster);

            await this.playStrategy.play(src);

            if (this._videoElement.muted && !this.services.video.isSoundMuted) {
                this._videoElement.muted = false;
            }

            this._analyticsReporter.reportPlayStarted();

        } catch (err) {
            if(isVideoFatalError(err)) {
                this._analyticsReporter.reportPlayError(err);
                throw err;
            }
        }
    }


    async retryPlay(): Promise<void> {
        try {
            await this.playStrategy.retryPlay();

            if(this._videoElement.muted && !this.services.video.isSoundMuted) {
                this._videoElement.muted = false;
            }

            this._analyticsReporter.reportRetryPlaySuccess();
        } catch (err) {
            this._analyticsReporter.reportRetryPlayError(err);
            throw err;
        }

    }

    async pause(): Promise<void> {
        this.playStrategy.pause();
    }

    async resume(): Promise<void> {
        try {
            await this.playStrategy.resume();
            this._analyticsReporter.reportResumePlaySuccess();
        } catch (err) {
            this._analyticsReporter.reportResumePlayError(err);
            throw err;
        }
    }


    mute(): void {
        this.videoElement.muted = true;
        this._analyticsReporter.reportMute();
    }

    unmute() {
        this.videoElement.muted = false;
        this._analyticsReporter.reportUnmute();
    }
}

